import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFarm } from '../../contexts/FarmContext';
import { recordsAPI, farmAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { 
  PlusIcon,
  FunnelIcon,
  BanknotesIcon,
  CurrencyDollarIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip
} from 'recharts';

const EggIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={props.className}>
    <path d="M12 3C8 3 4 9 4 13.5C4 17.64 7.36 21 11.5 21H12.5C16.64 21 20 17.64 20 13.5C20 9 16 3 12 3Z" stroke="currentColor" strokeWidth="1.5"/>
  </svg>
);

const typeOptions = [
  { value: 'egg_collection', label: 'Egg collection' },
  { value: 'feed_consumption', label: 'Feed consumption' },
  { value: 'sales', label: 'Sales' },
  { value: 'expense', label: 'Expense' },
  { value: 'mortality', label: 'Mortality' },
  { value: 'stock', label: 'Stock change' },
  { value: 'health_check', label: 'Health check' },
];

const expenseCategories = [
  'feed', 'medication', 'vaccination', 'utilities', 'labor', 'transport', 'maintenance', 'litter', 'rent', 'loan_interest', 'other'
];

const salesCategories = ['eggs', 'chickens', 'manure', 'equipment', 'other'];
const stockCategories = ['eggs', 'feed', 'vaccine', 'litter', 'equipment', 'chicks', 'other'];
const eggAdjustReasons = [
  { value: 'count_correction', label: 'Physical count' },
  { value: 'unrecorded_sale', label: 'Sold but not recorded' },
  { value: 'home_use', label: 'Home use / staff' },
  { value: 'broken', label: 'Broken or spoiled' },
  { value: 'gift', label: 'Given away' },
  { value: 'write_off', label: 'Write off old stock' }
];
const EGG_TRAY_SIZE = 30;
const feedTypes = ['chick mash','grower mash','finisher','layers mash','broiler starter','broiler finisher','pre-starter','developer','other'];
const feedBrandsKE = ['Unga Farm Care','Kenchic','Simba Feed','Pembe','Mombasa Maize Millers (Rafiki)','Sigma Feeds','Jenga Feeds','Saijiki','Wakala','Other'];
const commonSymptoms = ['lethargy','coughing','sneezing','runny eyes','diarrhea','reduced feed intake','reduced water intake','feather loss','lameness','swollen comb','blood in stool'];
const commonTreatments = ['multivitamins','electrolytes','antibiotics','dewormer','isolate sick birds','disinfect housing','adjust temperature','change feed','increase water','consult vet'];

const Records = () => {
  const { currentFarm } = useFarm();
  const canEdit = ['owner','manager','editor'].includes(currentFarm?.currentUserRole);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]); // filtered page for table
  const [trendItems, setTrendItems] = useState([]); // full set for trend chart based on current type
  const [allItemsForSummary, setAllItemsForSummary] = useState([]); // all types for tiles
  const [type, setType] = useState('expense');
  const [range, setRange] = useState({ start: '', end: '' });
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formType, setFormType] = useState('egg_collection');
  const [cohorts, setCohorts] = useState([]);
  const [cohortId, setCohortId] = useState('');
  const [editRec, setEditRec] = useState(null);
  const [birds, setBirds] = useState(null);
  const [eggStock, setEggStock] = useState(null);
  const [showEggAdjust, setShowEggAdjust] = useState(false);
  const [adjustingEggs, setAdjustingEggs] = useState(false);
  const [stockCategory, setStockCategory] = useState('other');
  const [eggAdjust, setEggAdjust] = useState({
    mode: 'set',
    date: '',
    trays: '',
    extras: '',
    traySize: '30',
    reason: 'count_correction',
    description: ''
  });
  // Sales form helpers
  const [salesQty, setSalesQty] = useState('');
  const [salesUnitPrice, setSalesUnitPrice] = useState('');
  const [salesAmountPaid, setSalesAmountPaid] = useState('');
  // Egg sales helpers
  const [salesCategory, setSalesCategory] = useState('eggs');
  const [eggTrays, setEggTrays] = useState('');
  const [eggExtras, setEggExtras] = useState('');
  const [eggTraySize, setEggTraySize] = useState('30');
  const [eggTrayPrice, setEggTrayPrice] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  // Column filters for table (date, type, flock already handled by top filters)
  const [columnFilters, setColumnFilters] = useState({
    quantity: '',
    amount: '',
    category: '',
    description: '',
    customer: '',
    phone: '',
    payment: '',
  });

  const load = async () => {
    if (!currentFarm) return;
    try {
      setLoading(true);
      try { const lc = await farmAPI.listCohorts(currentFarm.id); setCohorts(lc.data?.cohorts || []); } catch {}
      // Fetch items for chart/table (respect type) and all items for summary concurrently
      const paramsBase = { cohortId: cohortId || undefined, startDate: range.start || undefined, endDate: range.end || undefined };
      const [filteredRes, allRes] = await Promise.all([
        type
          ? recordsAPI.getRecordsByType(currentFarm.id, type, { ...paramsBase, limit: pageSize, offset: (page - 1) * pageSize })
          : recordsAPI.getRecords(currentFarm.id, { ...paramsBase, limit: pageSize, offset: (page - 1) * pageSize }),
        recordsAPI.getRecords(currentFarm.id, { ...paramsBase, limit: 999999 }) // Fetch all records for summary (no practical limit)
      ]);
      setItems(filteredRes.data.records || []);
      setTotal(Number(filteredRes.data.total || 0));
      setAllItemsForSummary(allRes.data.records || []);
      // Trend items: use full set filtered by current type (to avoid pagination affecting chart)
      const ti = (type ? (allRes.data.records || []).filter(r => r.type === type) : (allRes.data.records || []));
      setTrendItems(ti);
      try { const b = await recordsAPI.getBirds(currentFarm.id, { cohortId: cohortId || undefined }); setBirds(b.data); } catch {}
      try {
        const es = await recordsAPI.getEggStock(currentFarm.id, { cohortId: cohortId || undefined });
        setEggStock(es.data || null);
      } catch {
        setEggStock(null);
      }
    } catch (e) {
      console.error('Load records error', e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [currentFarm, type, range.start, range.end, cohortId, page, pageSize]);
  useEffect(() => { setPage(1); }, [type, range.start, range.end, cohortId]);
  const openedAdjustFromQuery = useRef(false);
  useEffect(() => {
    if (openedAdjustFromQuery.current || eggStock == null) return;
    if (new URLSearchParams(window.location.search).get('adjustEggs') === '1') {
      openedAdjustFromQuery.current = true;
      openEggAdjust();
    }
  }, [eggStock]);

  const download = async (fmt = 'pdf') => {
    if (!currentFarm) return;
    try {
      setDownloading(true);
      const params = { cohortId: cohortId || undefined, startDate: range.start || undefined, endDate: range.end || undefined, format: fmt };
      const res = await recordsAPI.exportRecords(currentFarm.id, params);
      const blob = new Blob([res.data], { type: fmt === 'csv' ? 'text/csv' : 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const ts = new Date().toISOString().slice(0,10);
      a.href = url;
      a.download = `records_${currentFarm.name?.replace(/\s+/g,'_') || 'farm'}_${ts}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Download error', e);
      alert('Failed to download records');
    } finally { setDownloading(false); }
  };

  const dailyNet = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    const next = new Date(today); next.setDate(next.getDate()+1);
    let s = 0, e = 0;
    items.forEach(r => {
      const d = new Date(r.date);
      if (d >= today && d < next) {
        if (r.type === 'sales') {
          // Use amountPaid for actual revenue (fallback to amount for old records)
          s += Number(r.metadata?.amountPaid ?? r.amount) || 0;
        }
        if (r.type === 'expense') e += Number(r.amount) || 0;
      }
    });
    return s - e;
  }, [items]);

  const onCreate = async (e) => {
    e.preventDefault();
    if (!currentFarm) return;
    const form = new FormData(e.currentTarget);
    const selectedType = form.get('type') || formType;

    const base = {
      type: selectedType,
      date: form.get('date'),
      description: form.get('description') || undefined,
    };

    let payload = { ...base };
    const chosenCohortId = (document.querySelector('select[name="cohortId"]') || {}).value || '';
    if (chosenCohortId) payload.cohortId = chosenCohortId;

    if (selectedType === 'egg_collection') {
      const trays = Number(form.get('trays') || 0);
      const extras = Number(form.get('extras') || 0);
      const broken = Number(form.get('broken') || 0);
      const traySize = Number(form.get('traySize') || 30);
      const total = trays * traySize + extras - broken;
      payload.quantity = Math.max(total, 0);
      payload.unit = 'eggs';
      payload.metadata = { trays, extras, broken, traySize };
      // no category for egg collection per request
    } else if (selectedType === 'feed_consumption') {
      payload.quantity = Number(form.get('quantity') || 0);
      payload.unit = 'kg';
      payload.amount = form.get('amount') ? Number(form.get('amount')) : undefined;
      payload.metadata = {
        ...(payload.metadata || {}),
        feedType: form.get('feedType') || undefined,
        feedBrand: form.get('feedBrand') === 'Other' ? (form.get('feedBrandOther') || 'Other') : form.get('feedBrand')
      };
      // remove currency for feed consumption
    } else if (selectedType === 'sales') {
      const selCategory = form.get('category') || 'other';
      let quantity = 0;
      let unitPrice = 0;
      let amount = 0;
      if (selCategory === 'eggs') {
        const trays = Number(form.get('eggTrays') || 0);
        const extras = Number(form.get('eggExtras') || 0);
        const traySize = Number(form.get('eggTraySize') || 30);
        const pricePerTray = Number(form.get('eggTrayPrice') || 0);
        if (trays < 0 || extras < 0 || traySize <= 0 || pricePerTray < 0) {
          alert('Please enter valid egg sale values.');
          return;
        }
        const totalEggs = Math.max(trays * traySize + extras, 0);
        const unitPerEgg = traySize > 0 ? pricePerTray / traySize : 0;
        quantity = totalEggs;
        unitPrice = unitPerEgg;
        amount = Number((unitPerEgg * totalEggs).toFixed(2));
        payload.metadata = {
          ...(payload.metadata || {}),
          eggSale: true,
          trays,
          extras,
          traySize,
          pricePerTray
        };
      } else {
        quantity = Number(form.get('quantity') || 0);
        unitPrice = Number(form.get('unitPrice') || 0);
        if (!quantity || quantity <= 0) { alert('Please enter a valid quantity for the sale.'); return; }
        if (unitPrice < 0) { alert('Unit price cannot be negative.'); return; }
        amount = Number((unitPrice * quantity).toFixed(2));
      }
      // Calculate amountPaid, balance, and auto-determine status
      const amountPaid = Number(form.get('amountPaid') || 0);
      if (amountPaid < 0) {
        alert('Amount paid cannot be negative.');
        return;
      }
      if (amountPaid > amount) {
        alert('Amount paid cannot exceed the sale amount.');
        return;
      }
      const paymentBalance = Math.max(0, amount - amountPaid);
      const paymentStatus = paymentBalance <= 0 ? 'paid' : 'pending';
      
      payload.amount = amount;
      payload.currency = 'KES';
      payload.quantity = quantity;
      payload.category = selCategory;
      payload.metadata = {
        ...(payload.metadata || {}),
        customerName: form.get('customerName') || undefined,
        customerPhone: form.get('customerPhone') || undefined,
        amountPaid,
        paymentBalance,
        paymentStatus
      };
      // no unit field in sales
    } else if (selectedType === 'expense') {
      payload.amount = Number(form.get('amount') || 0);
      payload.currency = 'KES';
      payload.category = form.get('category') || 'other';
    } else if (selectedType === 'mortality') {
      payload.quantity = Number(form.get('quantity') || 0) * -1;
      payload.unit = 'birds';
      // remove category for mortality
    } else if (selectedType === 'stock') {
      const cat = form.get('category') || 'other';
      payload.category = cat;
      if (cat === 'eggs') {
        const trays = Number(form.get('stockEggTrays') || 0);
        const extras = Number(form.get('stockEggExtras') || 0);
        const traySize = Number(form.get('stockEggTraySize') || 30);
        const dir = form.get('stockEggDirection') === 'remove' ? -1 : 1;
        payload.quantity = dir * Math.max(trays * traySize + extras, 0);
        payload.unit = 'eggs';
        payload.metadata = {
          eggAdjustment: true,
          trays,
          extras,
          traySize,
          reason: form.get('stockEggReason') || 'count_correction'
        };
        if (!payload.quantity) {
          alert('Enter trays or extra eggs to adjust stock.');
          return;
        }
      } else {
        payload.quantity = Number(form.get('quantity') || 0);
      }
    } else if (selectedType === 'health_check') {
      // Number affected captured in quantity; unit birds
      payload.quantity = Number(form.get('affected') || 0);
      payload.unit = 'birds';
      payload.metadata = {
        ...(payload.metadata || {}),
        bodyCondition: form.get('bodyCondition') || undefined,
        symptoms: (() => {
          const selected = form.getAll('symptoms') || [];
          const otherText = (form.get('symptomsOther') || '').trim();
          const others = otherText ? otherText.split(',').map(s => s.trim()).filter(Boolean) : [];
          return [...selected, ...others];
        })(),
        treatments: (() => {
          const selected = form.getAll('treatments') || [];
          const otherText = (form.get('treatmentsOther') || '').trim();
          const others = otherText ? otherText.split(',').map(s => s.trim()).filter(Boolean) : [];
          return [...selected, ...others];
        })()
      };
    }

    try {
      setCreating(true);
      await recordsAPI.createRecord(currentFarm.id, payload);
      // Reset the form BEFORE unmounting the modal to avoid throwing after success
      if (e.currentTarget) {
        try { e.currentTarget.reset(); } catch (_) {}
      }
      setFormType('egg_collection');
      setStockCategory('other');
      setShowCreate(false);
      load();
    } catch (err) {
      console.error('Create record error', err);
      alert(err.response?.data?.details || err.response?.data?.message || 'Failed to create record');
    } finally { setCreating(false); }
  };

  const todayLocalDate = () => {
    const d = new Date();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${m}-${day}`;
  };

  const openEggAdjust = () => {
    const closing = Math.max(0, Number(eggStock?.closingBalance || 0));
    const traySize = EGG_TRAY_SIZE;
    setEggAdjust({
      mode: 'set',
      date: todayLocalDate(),
      trays: String(Math.floor(closing / traySize) || ''),
      extras: String(closing % traySize || ''),
      traySize: String(traySize),
      reason: 'count_correction',
      description: ''
    });
    setShowEggAdjust(true);
  };

  const submitEggAdjust = async (e) => {
    e.preventDefault();
    if (!currentFarm) return;
    const trays = Number(eggAdjust.trays || 0);
    const extras = Number(eggAdjust.extras || 0);
    const traySize = Number(eggAdjust.traySize || EGG_TRAY_SIZE);
    const eggs = Math.max(trays * traySize + extras, 0);
    const payload = {
      date: eggAdjust.date,
      reason: eggAdjust.reason,
      description: eggAdjust.description || undefined,
      cohortId: cohortId || undefined,
      trays,
      extras,
      traySize
    };
    if (eggAdjust.mode === 'set') payload.actualQty = eggs;
    else payload.deltaQty = eggAdjust.mode === 'remove' ? -eggs : eggs;
    try {
      setAdjustingEggs(true);
      await recordsAPI.adjustEggStock(currentFarm.id, payload);
      setShowEggAdjust(false);
      load();
    } catch (err) {
      alert(err.response?.data?.details || err.response?.data?.message || 'Failed to adjust egg stock');
    } finally {
      setAdjustingEggs(false);
    }
  };

  const remove = async (id, recordInfo = null) => {
    const recordDesc = recordInfo 
      ? `Are you sure you want to delete this ${recordInfo.type} record${recordInfo.date ? ` from ${new Date(recordInfo.date).toLocaleDateString()}` : ''}?`
      : 'Are you sure you want to delete this record?';
    if (!window.confirm(`${recordDesc}\n\nThis action cannot be undone.`)) return false;
    try {
      await recordsAPI.deleteRecord(id);
      load();
      return true;
    } catch (e) {
      console.error('Delete record error', e);
      alert(e.response?.data?.message || 'Failed to delete record');
      return false;
    }
  };

  const summary = useMemo(() => {
    const s = { eggs: 0, feedKg: 0, sales: 0, salesTotal: 0, expenses: 0 };
    const source = allItemsForSummary;
    source.forEach(r => {
      const qty = Number(r.quantity) || 0;
      const amt = Number(r.amount) || 0;
      switch (r.type) {
        case 'egg_collection': s.eggs += qty; break;
        case 'feed_consumption': s.feedKg += qty; break;
        case 'sales': 
          // Use amountPaid for income calculations (actual revenue received)
          const amountPaid = Number(r.metadata?.amountPaid ?? amt); // fallback to amount for old records
          s.sales += amountPaid;
          s.salesTotal += amt; // Track total sale value separately
          break;
        case 'expense': s.expenses += amt; break;
        default: break;
      }
    });
    return s;
  }, [allItemsForSummary]);

  // Apply column filters to items for table display
  // (date, type, flock already filtered by top-level filters)
  const filteredItems = useMemo(() => {
    return items.filter(rec => {
      // Quantity filter - partial text match
      const qtyStr = String(rec.quantity ?? '');
      if (columnFilters.quantity && !qtyStr.includes(columnFilters.quantity)) return false;
      
      // Amount filter - partial text match
      const amtStr = String(rec.amount ?? '');
      if (columnFilters.amount && !amtStr.includes(columnFilters.amount)) return false;
      
      // Category filter - exact match from dropdown
      const catStr = (rec.category || '').toLowerCase();
      if (columnFilters.category && catStr !== columnFilters.category.toLowerCase()) return false;
      
      // Description filter - partial text match
      const descStr = (rec.description || '').toLowerCase();
      if (columnFilters.description && !descStr.includes(columnFilters.description.toLowerCase())) return false;
      
      // Customer filter - partial text match
      const customerStr = (rec.metadata?.customerName || '').toLowerCase();
      if (columnFilters.customer && !customerStr.includes(columnFilters.customer.toLowerCase())) return false;
      
      // Phone filter - partial text match
      const phoneStr = (rec.metadata?.customerPhone || '').toLowerCase();
      if (columnFilters.phone && !phoneStr.includes(columnFilters.phone.toLowerCase())) return false;
      
      // Payment filter - exact match from dropdown
      const paymentStr = (rec.metadata?.paymentStatus || 'pending').toLowerCase();
      if (columnFilters.payment && paymentStr !== columnFilters.payment.toLowerCase()) return false;

      return true;
    });
  }, [items, columnFilters]);

  const clearColumnFilters = () => {
    setColumnFilters({
      quantity: '',
      amount: '',
      category: '',
      description: '',
      customer: '',
      phone: '',
      payment: '',
    });
  };

  const hasColumnFilters = Object.values(columnFilters).some(v => v !== '');

  const trendTitle = useMemo(() => {
    switch (type) {
      case 'egg_collection': return 'Eggs Collected (last 12 months)';
      case 'feed_consumption': return 'Feed Used (kg) (last 12 months)';
      case 'sales': return 'Sales (KES) (last 12 months)';
      case 'expense': return 'Expenses (KES) (last 12 months)';
      case 'mortality': return 'Mortality (birds) (last 12 months)';
      case 'stock': return 'Stock Changes (last 12 months)';
      case 'health_check': return 'Affected Birds (last 12 months)';
      default: return 'Recent Trend';
    }
  }, [type]);

  const trendData = useMemo(() => {
    // Aggregate by month for the selected type over the last 12 months
    const source = trendItems.slice().reverse();
    const now = new Date();
    const monthKeys = [];
    const labels = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; // YYYY-MM
      const label = d.toLocaleString(undefined, { month: 'short', year: 'numeric' });
      monthKeys.push(key);
      labels.push(label);
    }
    const byMonth = new Map(monthKeys.map((k, idx) => [k, { paid: 0, pending: 0, value: 0, label: labels[idx] }]));

    source.forEach(r => {
      const d = new Date(r.date);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const bucket = byMonth.get(key);
      if (!bucket) return;
      if (type === 'sales') {
        const totalAmount = Number(r.amount) || 0;
        const amountPaid = Number(r.metadata?.amountPaid ?? totalAmount) || 0; // fallback for old records
        const balance = Number(r.metadata?.paymentBalance ?? 0) || 0;
        bucket.paid += amountPaid;
        bucket.pending += balance;
        bucket.value = (bucket.paid || 0) + (bucket.pending || 0);
        return;
      }
      let val = 0;
      if (type === 'egg_collection') val = Number(r.quantity) || 0;
      else if (type === 'feed_consumption') val = Number(r.quantity) || 0;
      else if (type === 'expense') val = Number(r.amount) || 0;
      else if (type === 'mortality') val = Math.abs(Number(r.quantity) || 0);
      else if (type === 'stock') val = Number(r.quantity) || 0;
      else if (type === 'health_check') val = Number(r.quantity) || 0;
      else {
        val = r.amount != null ? Number(r.amount) : (r.quantity != null ? Number(r.quantity) : 0);
      }
      const safe = isNaN(val) ? 0 : val;
      bucket.paid += safe; // keep single series in 'paid' to reuse existing chart config
      bucket.value = (bucket.paid || 0); // ensure value mirrors series for non-sales
    });

    return Array.from(byMonth.values()).map(v => ({ name: v.label, paid: v.paid, pending: v.pending, value: v.value }));
  }, [items, type]);

  const renderDynamicFields = () => {
    switch (formType) {
      case 'egg_collection':
        return (
          <>
            <div>
              <label className="label">Trays *</label>
              <input name="trays" type="number" min="0" className="input" placeholder="e.g., 10"/>
            </div>
            <div>
              <label className="label">Tray size</label>
              <input name="traySize" type="number" min="1" defaultValue={30} className="input"/>
            </div>
            <div>
              <label className="label">Extra eggs</label>
              <input name="extras" type="number" min="0" className="input" placeholder="e.g., 4"/>
            </div>
            <div>
              <label className="label">Broken</label>
              <input name="broken" type="number" min="0" className="input" placeholder="e.g., 2"/>
            </div>
          </>
        );
      case 'feed_consumption':
        return (
          <>
            <div>
              <label className="label">Quantity (kg) *</label>
              <input name="quantity" type="number" step="any" min="0" className="input" placeholder="e.g., 25"/>
            </div>
            <div>
              <label className="label">Feed type</label>
              <select name="feedType" className="input">
                {feedTypes.map(ft => <option key={ft} value={ft}>{ft}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Feed brand</label>
              <select name="feedBrand" className="input">
                {feedBrandsKE.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="label">If brand is Other, specify</label>
              <input name="feedBrandOther" className="input" placeholder="Brand name"/>
            </div>
            <div className="md:col-span-2">
              <label className="label">Amount (optional)</label>
              <input name="amount" type="number" step="0.01" className="input" placeholder="e.g., 18.50"/>
            </div>
          </>
        );
      case 'sales':
        return (
          <>
            <div>
              <label className="label">Category *</label>
              <select name="category" required className="input" value={salesCategory} onChange={(e)=>setSalesCategory(e.target.value)}>
                {salesCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {salesCategory === 'eggs' ? (
              <>
                <div>
                  <label className="label">Trays *</label>
                  <input name="eggTrays" type="number" min="0" required className="input" placeholder="e.g., 10" value={eggTrays} onChange={(e)=>setEggTrays(e.target.value)} />
                </div>
                <div>
                  <label className="label">Extra eggs</label>
                  <input name="eggExtras" type="number" min="0" className="input" placeholder="e.g., 4" value={eggExtras} onChange={(e)=>setEggExtras(e.target.value)} />
                </div>
                <div>
                  <label className="label">Tray size</label>
                  <input name="eggTraySize" type="number" min="1" className="input" value={eggTraySize} onChange={(e)=>setEggTraySize(e.target.value)} />
                </div>
                <div>
                  <label className="label">Price per tray (KES) *</label>
                  <input name="eggTrayPrice" type="number" step="0.01" min="0" required className="input" placeholder="e.g., 420" value={eggTrayPrice} onChange={(e)=>setEggTrayPrice(e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Amount (auto)</label>
                  <input readOnly className="input bg-gray-50" value={(() => {
                    const trays = Number(eggTrays || 0);
                    const extras = Number(eggExtras || 0);
                    const size = Number(eggTraySize || 30);
                    const trayPrice = Number(eggTrayPrice || 0);
                    const totalEggs = Math.max(trays * size + extras, 0);
                    const perEgg = size > 0 ? trayPrice / size : 0;
                    const amt = isNaN(totalEggs*perEgg) ? 0 : totalEggs * perEgg;
                    return `KES ${amt.toFixed(2)}`;
                  })()} />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="label">Quantity *</label>
                  <input name="quantity" type="number" step="any" min="0" required className="input" placeholder="e.g., 120" value={salesQty} onChange={(e)=>setSalesQty(e.target.value)} />
                </div>
                <div>
                  <label className="label">Unit price (KES) *</label>
                  <input name="unitPrice" type="number" step="0.01" min="0" required className="input" placeholder="e.g., 15.00" value={salesUnitPrice} onChange={(e)=>setSalesUnitPrice(e.target.value)} />
                </div>
                <div className="md:col-span-2">
                  <label className="label">Amount (auto)</label>
                  <input readOnly className="input bg-gray-50" value={(() => {
                    const q = Number(salesQty || 0);
                    const u = Number(salesUnitPrice || 0);
                    const a = isNaN(q*u) ? 0 : q*u;
                    return `KES ${a.toFixed(2)}`;
                  })()} />
                </div>
              </>
            )}
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="label">Customer name</label>
                <input name="customerName" className="input" placeholder="Customer full name"/>
              </div>
              <div>
                <label className="label">Customer phone</label>
                <input name="customerPhone" className="input" placeholder="e.g., +2547xxxxxxxx" pattern="^[0-9+ ()-]{7,20}$" title="Enter a valid phone number" />
              </div>
            </div>
            <div>
              <label className="label">Amount Paid *</label>
              <input name="amountPaid" type="number" step="0.01" min="0" className="input" placeholder="e.g., 500.00" value={salesAmountPaid} onChange={(e)=>setSalesAmountPaid(e.target.value)} />
              <p className="text-xs text-gray-500 mt-1">
                Balance will be auto-calculated. Status: {(() => {
                  const total = salesCategory === 'eggs' 
                    ? (Number(eggTrays || 0) * Number(eggTraySize || 30) + Number(eggExtras || 0)) * (Number(eggTrayPrice || 0) / Number(eggTraySize || 30))
                    : Number(salesQty || 0) * Number(salesUnitPrice || 0);
                  const paid = Number(salesAmountPaid || 0);
                  const balance = Math.max(0, total - paid);
                  return balance <= 0 ? '✓ Paid' : `Pending (Balance: KES ${balance.toFixed(2)})`;
                })()}
              </p>
            </div>
          </>
        );
      case 'expense':
        return (
          <>
            <div>
              <label className="label">Category *</label>
              <select name="category" required className="input">
                {expenseCategories.map(c => <option key={c} value={c}>{c.replace('_',' ')}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Amount *</label>
              <input name="amount" type="number" step="0.01" defaultValue={0} required className="input" placeholder="e.g., 40.00"/>
            </div>
          </>
        );
      case 'mortality':
        return (
          <>
            <div className="md:col-span-2">
              <label className="label">Birds (count) *</label>
              <input name="quantity" type="number" min="0" required className="input"/>
            </div>
          </>
        );
      case 'stock':
        return (
          <>
            <div>
              <label className="label">Category</label>
              <select name="category" className="input" value={stockCategory} onChange={(e)=>setStockCategory(e.target.value)}>
                {stockCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            {stockCategory === 'eggs' ? (
              <>
                <div>
                  <label className="label">Direction</label>
                  <select name="stockEggDirection" className="input">
                    <option value="add">Add eggs</option>
                    <option value="remove">Remove eggs</option>
                  </select>
                </div>
                <div>
                  <label className="label">Trays</label>
                  <input name="stockEggTrays" type="number" min="0" className="input" placeholder="e.g., 4"/>
                </div>
                <div>
                  <label className="label">Extra eggs</label>
                  <input name="stockEggExtras" type="number" min="0" className="input" placeholder="e.g., 6"/>
                </div>
                <div>
                  <label className="label">Tray size</label>
                  <input name="stockEggTraySize" type="number" min="1" defaultValue={30} className="input"/>
                </div>
                <div>
                  <label className="label">Reason</label>
                  <select name="stockEggReason" className="input">
                    {eggAdjustReasons.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <div>
                <label className="label">Quantity *</label>
                <input name="quantity" type="number" step="any" required className="input"/>
              </div>
            )}
          </>
        );
      case 'health_check':
        return (
          <>
            <div>
              <label className="label">Affected birds *</label>
              <input name="affected" type="number" min="0" required className="input" placeholder="e.g., 5"/>
            </div>
            <div>
              <label className="label">Body condition</label>
              <select name="bodyCondition" className="input">
                {['good','fair','poor'].map(x => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="label">Symptoms (select all that apply)</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {commonSymptoms.map(sym => (
                  <label key={sym} className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" name="symptoms" value={sym} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"/>
                    {sym}
                  </label>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="label">Other symptoms (comma separated)</label>
              <input name="symptomsOther" className="input" placeholder="e.g., weight loss, ruffled feathers"/>
            </div>
            <div className="md:col-span-2">
              <label className="label">Treatment / Actions (select all that apply)</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {commonTreatments.map(act => (
                  <label key={act} className="flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" name="treatments" value={act} className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"/>
                    {act}
                  </label>
                ))}
              </div>
            </div>
            <div className="md:col-span-2">
              <label className="label">Other treatments/actions (comma separated)</label>
              <input name="treatmentsOther" className="input" placeholder="e.g., probiotics, vitamin K"/>
            </div>
          </>
        );
    }
  };

  const onUpdate = async (e) => {
    e.preventDefault();
    if (!editRec) return;
    const form = new FormData(e.currentTarget);
    const typeSel = form.get('type');
    const cohortIdValue = form.get('cohortId');
    const base = {
      type: typeSel,
      date: form.get('date'),
      description: form.get('description') || undefined,
      cohortId: cohortIdValue && cohortIdValue.trim() !== '' ? cohortIdValue : null,
    };
    let payload = { ...base };
    if (typeSel === 'egg_collection') {
      const trays = Number(form.get('trays') || 0);
      const extras = Number(form.get('extras') || 0);
      const broken = Number(form.get('broken') || 0);
      const traySize = Number(form.get('traySize') || 30);
      const total = trays * traySize + extras - broken;
      payload.quantity = Math.max(total, 0);
      payload.unit = 'eggs';
      payload.metadata = { trays, extras, broken, traySize };
    } else if (typeSel === 'feed_consumption') {
      payload.quantity = Number(form.get('quantity') || 0);
      payload.unit = 'kg';
      payload.amount = form.get('amount') ? Number(form.get('amount')) : undefined;
      payload.metadata = { feedType: form.get('feedType') || undefined, feedBrand: form.get('feedBrand') || undefined };
    } else if (typeSel === 'sales') {
      const selCategory = form.get('category') || editRec.category || 'other';
      let quantity = 0; let amount = 0; let mdExtra = {};
      if (selCategory === 'eggs') {
        const trays = Number(form.get('eggTrays') || editRec.metadata?.trays || 0);
        const extras = Number(form.get('eggExtras') || editRec.metadata?.extras || 0);
        const traySize = Number(form.get('eggTraySize') || editRec.metadata?.traySize || 30);
        const pricePerTray = Number(form.get('eggTrayPrice') || editRec.metadata?.pricePerTray || 0);
        const perEgg = traySize > 0 ? pricePerTray / traySize : 0;
        const totalEggs = Math.max(trays * traySize + extras, 0);
        quantity = totalEggs;
        amount = Number((totalEggs * perEgg).toFixed(2));
        mdExtra = { eggSale: true, trays, extras, traySize, pricePerTray };
      } else {
        const q = Number(form.get('quantity') || editRec.quantity || 0);
        const u = Number(form.get('unitPrice') || 0);
        quantity = q;
        amount = u > 0 ? Number((q * u).toFixed(2)) : Number(editRec.amount || 0);
      }
      // Allow manual amount override
      const manualAmount = form.get('amount');
      if (manualAmount && Number(manualAmount) > 0) {
        amount = Number(manualAmount);
      }
      // Calculate amountPaid, balance, and auto-determine status
      const amountPaid = Number(form.get('amountPaid') ?? editRec.metadata?.amountPaid ?? 0);
      const paymentBalance = Math.max(0, amount - amountPaid);
      const paymentStatus = paymentBalance <= 0 ? 'paid' : 'pending';
      
      payload.amount = amount;
      payload.currency = 'KES';
      payload.quantity = quantity;
      payload.category = selCategory;
      payload.metadata = { 
        ...(editRec.metadata || {}), 
        ...mdExtra, 
        customerName: form.get('customerName') || editRec.metadata?.customerName || undefined, 
        customerPhone: form.get('customerPhone') || editRec.metadata?.customerPhone || undefined, 
        amountPaid,
        paymentBalance,
        paymentStatus
      };
    } else if (typeSel === 'expense') {
      payload.amount = Number(form.get('amount') || 0);
      payload.currency = 'KES';
      payload.category = form.get('category') || 'other';
    } else if (typeSel === 'mortality') {
      payload.quantity = Number(form.get('quantity') || 0) * -1;
      payload.unit = 'birds';
    } else if (typeSel === 'stock') {
      payload.quantity = Number(form.get('quantity') || 0);
      payload.category = form.get('category') || 'other';
    } else if (typeSel === 'health_check') {
      payload.quantity = Number(form.get('affected') || 0);
      payload.unit = 'birds';
      payload.metadata = {
        bodyCondition: form.get('bodyCondition') || undefined,
        symptoms: (form.get('symptoms') || '').toString().split(',').map(s => s.trim()).filter(Boolean),
        treatments: (form.get('treatments') || '').toString().split(',').map(s => s.trim()).filter(Boolean)
      };
    }
    try {
      await recordsAPI.updateRecord(editRec.id, payload);
      setEditRec(null);
      load();
    } catch (err) {
      console.error('Update record error', err);
      alert(err.response?.data?.details || err.response?.data?.message || 'Failed to update record');
    }
  };

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
              <BeakerIcon className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Records</h1>
              <p className="text-green-100">Track egg collections, feed consumption, finances</p>
            </div>
          </div>
          <button 
            className={`inline-flex items-center px-5 py-2.5 bg-white text-green-700 rounded-xl hover:bg-green-50 transition-colors font-medium ${!canEdit ? 'opacity-50 cursor-not-allowed' : ''}`}
            onClick={()=>{ if (!canEdit) return; setShowCreate(true); setFormType('egg_collection'); }} 
            disabled={!canEdit}
          >
            <PlusIcon className="h-5 w-5 mr-2"/>
            Add Record
          </button>
        </div>
        
        {/* Stats in header */}
        <div className="relative mt-6 grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div className="bg-white/10 backdrop-blur rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <EggIcon className="h-4 w-4 text-amber-300"/>
              <span className="text-xs text-green-200">Eggs</span>
            </div>
            <p className="text-xl font-bold">{summary.eggs}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <BeakerIcon className="h-4 w-4 text-blue-300"/>
              <span className="text-xs text-green-200">Feed (kg)</span>
            </div>
            <p className="text-xl font-bold">{summary.feedKg}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <CurrencyDollarIcon className="h-4 w-4 text-emerald-300"/>
              <span className="text-xs text-green-200">Sales</span>
            </div>
            <p className="text-xl font-bold">KES {summary.sales.toFixed(0)}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <BanknotesIcon className="h-4 w-4 text-rose-300"/>
              <span className="text-xs text-green-200">Expenses</span>
            </div>
            <p className="text-xl font-bold">KES {summary.expenses.toFixed(0)}</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3">
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs text-green-200">Net Birds</span>
                <p className="text-xl font-bold">{birds?.netBirds ?? '—'}</p>
              </div>
              <div className="text-right text-xs text-green-200">
                <div>+{birds?.stockAdd ?? 0}</div>
                <div>-{birds?.mortality ?? 0}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {(() => {
        const closing = Number(eggStock?.closingBalance);
        const hasStock = Number.isFinite(closing);
        const trays = hasStock ? Math.floor(Math.max(closing, 0) / EGG_TRAY_SIZE) : 0;
        const rem = hasStock ? Math.max(closing, 0) % EGG_TRAY_SIZE : 0;
        return (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 sm:p-5">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-amber-800">
                  {cohortId ? 'Egg stock for this flock' : 'Egg stock on hand'}
                </p>
                <p className="text-3xl font-bold text-amber-900">
                  {hasStock ? closing.toLocaleString() : '—'}
                </p>
                {hasStock && (
                  <p className="text-sm text-amber-700 mt-1">
                    {trays} trays of {EGG_TRAY_SIZE} + {rem} eggs
                    <span className="ml-2">· collected {Number(eggStock?.collected || 0).toLocaleString()} · sold {Number(eggStock?.sold || 0).toLocaleString()}</span>
                  </p>
                )}
              </div>
              {canEdit && (
                <button
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors text-sm font-medium self-start"
                  onClick={openEggAdjust}
                >
                  Adjust stock
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400"/>
            <select className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" value={type} onChange={(e)=>setType(e.target.value)}>
              <option value="">All Types</option>
              {typeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <select className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all w-full sm:w-64" value={cohortId} onChange={(e)=>setCohortId(e.target.value)}>
            <option value="">All Flocks</option>
            {cohorts.map(c => <option key={c.id} value={c.id}>{c.name} • {c.type}</option>)}
          </select>
          <input type="date" className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" value={range.start} onChange={(e)=>setRange(prev=>({...prev, start:e.target.value}))}/>
          <span className="text-gray-500">to</span>
          <input type="date" className="px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all" value={range.end} onChange={(e)=>setRange(prev=>({...prev, end:e.target.value}))}/>
          <div className="ml-auto flex items-center gap-2">
            <button className="px-4 py-2 bg-green-50 text-green-700 rounded-xl hover:bg-green-100 font-medium text-sm transition-colors" disabled={downloading} onClick={()=>download('pdf')}>{downloading ? 'Preparing…' : 'PDF'}</button>
            <button className="px-4 py-2 bg-gray-50 text-gray-700 rounded-xl hover:bg-gray-100 font-medium text-sm transition-colors" disabled={downloading} onClick={()=>download('csv')}>CSV</button>
          </div>
        </div>
      </div>

      {/* Mobile condensed cards */}
      <div className="space-y-3 md:hidden">
        {loading ? (
          <div className="flex items-center justify-center h-40"><LoadingSpinner/></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No records found.</div>
        ) : (
          items.map((rec) => (
            <div key={rec.id} className="border rounded-lg p-4 bg-white shadow-sm">
              <div className="flex items-center justify-between">
                <div className="font-medium text-gray-900 capitalize">{rec.type.replace('_',' ')}</div>
                <div className="text-xs text-gray-500">{new Date(rec.date).toLocaleDateString()}</div>
              </div>
              {rec.cohort?.name && (
                <div className="text-xs text-gray-500 mt-1">Flock: {rec.cohort.name}</div>
              )}
              
              {/* Type-specific fields */}
              <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                {rec.type === 'egg_collection' ? (
                  <>
                    <div className="text-gray-700">Trays: <span className="font-medium">{rec.metadata?.trays ?? '—'}</span></div>
                    <div className="text-gray-700">Tray Size: <span className="font-medium">{rec.metadata?.traySize ?? 30}</span></div>
                    <div className="text-gray-700">Extras: <span className="font-medium">{rec.metadata?.extras ?? 0}</span></div>
                    <div className="text-gray-700">Broken: <span className="font-medium">{rec.metadata?.broken ?? 0}</span></div>
                    <div className="text-gray-700 col-span-2 font-semibold">Total: <span className="text-green-600">{rec.quantity ?? '—'} eggs</span></div>
                  </>
                ) : rec.type === 'feed_consumption' ? (
                  <>
                    <div className="text-gray-700">Quantity: <span className="font-medium">{rec.quantity ?? '—'} kg</span></div>
                    <div className="text-gray-700">Feed Type: <span className="font-medium">{rec.metadata?.feedType || '—'}</span></div>
                    <div className="text-gray-700">Brand: <span className="font-medium">{rec.metadata?.feedBrand || '—'}</span></div>
                    {rec.amount != null && <div className="text-gray-700">Amount: <span className="font-medium">{rec.currency || 'KES'} {rec.amount}</span></div>}
                  </>
                ) : rec.type === 'health_check' ? (
                  <>
                    <div className="text-gray-700">Affected: <span className="font-medium">{rec.quantity ?? 0} birds</span></div>
                    <div className="text-gray-700">Condition: <span className="font-medium">{rec.metadata?.bodyCondition || '—'}</span></div>
                    {rec.metadata?.symptoms?.length > 0 && (
                      <div className="text-gray-700 col-span-2">Symptoms: <span className="font-medium">{Array.isArray(rec.metadata.symptoms) ? rec.metadata.symptoms.join(', ') : rec.metadata.symptoms}</span></div>
                    )}
                    {rec.metadata?.treatments?.length > 0 && (
                      <div className="text-gray-700 col-span-2">Treatment: <span className="font-medium">{Array.isArray(rec.metadata.treatments) ? rec.metadata.treatments.join(', ') : rec.metadata.treatments}</span></div>
                    )}
                  </>
                ) : rec.type === 'sales' ? (
                  <>
                    {rec.category === 'eggs' ? (
                      <>
                        <div className="text-gray-700">Trays: <span className="font-medium">{rec.metadata?.trays ?? '—'}</span></div>
                        <div className="text-gray-700">Extras: <span className="font-medium">{rec.metadata?.extras ?? 0}</span></div>
                        <div className="text-gray-700">Tray Size: <span className="font-medium">{rec.metadata?.traySize ?? 30}</span></div>
                        <div className="text-gray-700">Price/Tray: <span className="font-medium">KES {rec.metadata?.pricePerTray ?? '—'}</span></div>
                      </>
                    ) : (
                      <>
                        <div className="text-gray-700">Quantity: <span className="font-medium">{rec.quantity ?? '—'}</span></div>
                        <div className="text-gray-700">Category: <span className="font-medium">{rec.category || '—'}</span></div>
                      </>
                    )}
                    <div className="text-gray-700 col-span-2 font-semibold">Total: <span className="text-gray-900">{rec.currency || 'KES'} {rec.amount}</span></div>
                    <div className="text-gray-700">Paid: <span className="font-medium text-green-600">{rec.currency || 'KES'} {rec.metadata?.amountPaid ?? 0}</span></div>
                    <div className="text-gray-700">Balance: <span className={`font-medium ${(rec.metadata?.paymentBalance ?? 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>{rec.currency || 'KES'} {rec.metadata?.paymentBalance ?? 0}</span></div>
                    {rec.metadata?.customerName && (
                      <div className="text-gray-700 col-span-2">Customer: <span className="font-medium">{rec.metadata.customerName}</span></div>
                    )}
                    {rec.metadata?.customerPhone && (
                      <div className="text-gray-700 col-span-2">Phone: <span className="font-medium">{rec.metadata.customerPhone}</span></div>
                    )}
                    <div className="col-span-2 text-xs flex items-center gap-2">
                      <span className="text-gray-600">Status:</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        rec.metadata?.paymentStatus === 'paid' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {rec.metadata?.paymentStatus === 'paid' ? '✓ Paid' : '⏳ Pending'}
                      </span>
                    </div>
                  </>
                ) : rec.type === 'expense' ? (
                  <>
                    <div className="text-gray-700">Category: <span className="font-medium capitalize">{(rec.category || '—').replace('_', ' ')}</span></div>
                    <div className="text-gray-700 font-semibold">Amount: <span className="text-red-600">{rec.currency || 'KES'} {rec.amount}</span></div>
                  </>
                ) : rec.type === 'mortality' ? (
                  <>
                    <div className="text-gray-700 col-span-2 font-semibold">Birds Lost: <span className="text-red-600">{Math.abs(rec.quantity ?? 0)}</span></div>
                  </>
                ) : rec.type === 'stock' ? (
                  <>
                    <div className="text-gray-700">Category: <span className="font-medium">{rec.category || '—'}</span></div>
                    <div className="text-gray-700">Quantity: <span className="font-medium">{rec.quantity ?? '—'}</span></div>
                  </>
                ) : (
                  <>
                    {rec.quantity != null && <div className="text-gray-700">Qty: <span className="font-medium">{rec.quantity} {rec.unit || ''}</span></div>}
                    {rec.amount != null && <div className="text-gray-700">Amount: <span className="font-medium">{rec.currency || 'KES'} {rec.amount}</span></div>}
                    {rec.category && <div className="text-gray-700 col-span-2">Category: <span className="font-medium">{rec.category}</span></div>}
                  </>
                )}
              </div>

              {/* Sales payment controls - only for manual sales (not shop orders which are already paid) */}
              {rec.type === 'sales' && !rec.metadata?.shopOrderId && (
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-xs text-gray-500">Amount Paid:</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Amount paid"
                    className="input py-1 px-2 text-sm w-28"
                    defaultValue={rec.metadata?.amountPaid ?? 0}
                    onBlur={async (e) => {
                      try {
                        const amountPaid = Number(e.target.value || 0);
                        if (amountPaid < 0) return;
                        const totalAmount = Number(rec.amount || 0);
                        if (amountPaid > totalAmount) {
                          alert('Amount paid cannot exceed total amount');
                          e.target.value = rec.metadata?.amountPaid ?? 0;
                          return;
                        }
                        const paymentBalance = Math.max(0, totalAmount - amountPaid);
                        const paymentStatus = paymentBalance <= 0 ? 'paid' : 'pending';
                        const newMd = { ...(rec.metadata || {}), amountPaid, paymentBalance, paymentStatus };
                        await recordsAPI.updateRecord(rec.id, { metadata: newMd });
                        load();
                      } catch (err) {
                        console.error('Update amount paid error', err);
                        alert('Failed to update amount paid');
                      }
                    }}
                  />
                </div>
              )}
              
              {/* Show payment status badge for shop order sales */}
              {rec.type === 'sales' && rec.metadata?.shopOrderId && (
                <div className="mt-2">
                  <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                    Paid via {rec.metadata?.paymentMethod === 'mpesa' ? 'M-Pesa' : 'Platform'}
                  </span>
                </div>
              )}

              {rec.description && <div className="mt-2 text-sm text-gray-600 italic">{rec.description}</div>}
              <div className="mt-3 flex items-center justify-end gap-2">
                <button className="btn-outline text-sm" onClick={()=> setEditRec(rec)}>Edit</button>
                <button className="btn-outline text-sm text-red-600 border-red-200 hover:bg-red-50" onClick={()=>remove(rec.id, rec)}>Delete</button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop table */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-4 hidden md:block">
        <h2 className="text-lg font-medium text-gray-900 mb-2">{trendTitle}</h2>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trendData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorPaid" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#16a34a" stopOpacity={0.35}/>
                  <stop offset="95%" stopColor="#16a34a" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35}/>
                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 12 }} stroke="#9ca3af" />
              <Tooltip />
              {type === 'sales' ? (
                <>
                  <Area type="monotone" stackId="1" dataKey="pending" stroke="#f59e0b" fillOpacity={1} fill="url(#colorPending)" />
                  <Area type="monotone" stackId="1" dataKey="paid" stroke="#16a34a" fillOpacity={1} fill="url(#colorPaid)" />
                </>
              ) : (
                <Area type="monotone" dataKey="value" stroke="#0284c7" fillOpacity={1} fill="url(#colorPaid)" />
              )}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm hidden md:block">
        {loading ? (
          <div className="flex items-center justify-center h-40"><LoadingSpinner/></div>
        ) : items.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No records found.</div>
        ) : (
          <>
          {/* Column filter controls */}
          {hasColumnFilters && (
            <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
              <span className="text-sm text-blue-700">
                Showing {filteredItems.length} of {items.length} records (filtered)
              </span>
              <button 
                className="text-sm text-blue-600 hover:text-blue-800 underline"
                onClick={clearColumnFilters}
              >
                Clear column filters
              </button>
            </div>
          )}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Flock</th>
                {type === 'egg_collection' ? (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Trays</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Tray Size</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Extras</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Broken</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Total</th>
                  </>
                ) : type === 'feed_consumption' ? (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Quantity (kg)</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Feed Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Brand</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Amount</th>
                  </>
                ) : type === 'health_check' ? (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Affected</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Body</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Symptoms</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Actions Taken</th>
                  </>
                ) : (
                  <>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Quantity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Category</th>
                {type === 'sales' && (
                  <>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Trays</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Extras</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Tray Size</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Price/Tray</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Phone</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Paid</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Balance</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                  </>
                )}
                  </>
                )}
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Description</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
              </tr>
              {/* Filter row */}
              <tr className="bg-gray-100">
                <th className="px-2 py-2"></th>
                <th className="px-2 py-2"></th>
                <th className="px-2 py-2"></th>
                {type === 'egg_collection' ? (
                  <>
                    <th className="px-2 py-2"></th>
                    <th className="px-2 py-2"></th>
                    <th className="px-2 py-2"></th>
                    <th className="px-2 py-2"></th>
                    <th className="px-2 py-2">
                      <input 
                        type="text" 
                        placeholder="Total..." 
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        value={columnFilters.quantity}
                        onChange={(e) => setColumnFilters(prev => ({...prev, quantity: e.target.value}))}
                      />
                    </th>
                  </>
                ) : type === 'feed_consumption' ? (
                  <>
                    <th className="px-2 py-2">
                      <input 
                        type="text" 
                        placeholder="Filter qty..." 
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        value={columnFilters.quantity}
                        onChange={(e) => setColumnFilters(prev => ({...prev, quantity: e.target.value}))}
                      />
                    </th>
                    <th className="px-2 py-2"></th>
                    <th className="px-2 py-2"></th>
                    <th className="px-2 py-2">
                      <input 
                        type="text" 
                        placeholder="Filter amt..." 
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        value={columnFilters.amount}
                        onChange={(e) => setColumnFilters(prev => ({...prev, amount: e.target.value}))}
                      />
                    </th>
                  </>
                ) : type === 'health_check' ? (
                  <>
                    <th className="px-2 py-2">
                      <input 
                        type="text" 
                        placeholder="Filter..." 
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        value={columnFilters.quantity}
                        onChange={(e) => setColumnFilters(prev => ({...prev, quantity: e.target.value}))}
                      />
                    </th>
                    <th className="px-2 py-2"></th>
                    <th className="px-2 py-2"></th>
                    <th className="px-2 py-2"></th>
                  </>
                ) : (
                  <>
                    <th className="px-2 py-2">
                      <input 
                        type="text" 
                        placeholder="Filter qty..." 
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        value={columnFilters.quantity}
                        onChange={(e) => setColumnFilters(prev => ({...prev, quantity: e.target.value}))}
                      />
                    </th>
                    <th className="px-2 py-2">
                      <input 
                        type="text" 
                        placeholder="Filter amt..." 
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                        value={columnFilters.amount}
                        onChange={(e) => setColumnFilters(prev => ({...prev, amount: e.target.value}))}
                      />
                    </th>
                    <th className="px-2 py-2">
                      <select 
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                        value={columnFilters.category}
                        onChange={(e) => setColumnFilters(prev => ({...prev, category: e.target.value}))}
                      >
                        <option value="">All categories</option>
                        {type === 'sales' && salesCategories.map(c => <option key={c} value={c}>{c}</option>)}
                        {type === 'expense' && expenseCategories.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                        {type === 'stock' && stockCategories.map(c => <option key={c} value={c}>{c}</option>)}
                        {!type && [...new Set([...salesCategories, ...expenseCategories, ...stockCategories])].map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                      </select>
                    </th>
                    {type === 'sales' && (
                      <>
                        <th className="px-2 py-2"></th>
                        <th className="px-2 py-2"></th>
                        <th className="px-2 py-2"></th>
                        <th className="px-2 py-2"></th>
                        <th className="px-2 py-2">
                          <input 
                            type="text" 
                            placeholder="Customer..." 
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                            value={columnFilters.customer}
                            onChange={(e) => setColumnFilters(prev => ({...prev, customer: e.target.value}))}
                          />
                        </th>
                        <th className="px-2 py-2">
                          <input 
                            type="text" 
                            placeholder="Phone..." 
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                            value={columnFilters.phone}
                            onChange={(e) => setColumnFilters(prev => ({...prev, phone: e.target.value}))}
                          />
                        </th>
                        <th className="px-2 py-2">
                          <select 
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 bg-white"
                            value={columnFilters.payment}
                            onChange={(e) => setColumnFilters(prev => ({...prev, payment: e.target.value}))}
                          >
                            <option value="">All</option>
                            <option value="paid">Paid</option>
                            <option value="pending">Pending</option>
                          </select>
                        </th>
                        <th className="px-2 py-2"></th>
                      </>
                    )}
                  </>
                )}
                <th className="px-2 py-2">
                  <input 
                    type="text" 
                    placeholder="Description..." 
                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                    value={columnFilters.description}
                    onChange={(e) => setColumnFilters(prev => ({...prev, description: e.target.value}))}
                  />
                </th>
                <th className="px-2 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredItems.length === 0 && hasColumnFilters && (
                <tr>
                  <td colSpan={20} className="px-4 py-8 text-center text-gray-500">
                    No records match your column filters. 
                    <button 
                      className="ml-2 text-primary-600 hover:text-primary-800 underline"
                      onClick={clearColumnFilters}
                    >
                      Clear filters
                    </button>
                  </td>
                </tr>
              )}
              {filteredItems.map(rec => (
                <tr key={rec.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-700">{new Date(rec.date).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-sm capitalize text-gray-700">{rec.type.replace('_',' ')}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">{rec.cohort ? `${rec.cohort.name}` : '—'}</td>
                  {type === 'egg_collection' ? (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.metadata?.trays ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.metadata?.traySize ?? 30}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.metadata?.extras ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.metadata?.broken ?? 0}</td>
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{rec.quantity ?? '—'}</td>
                    </>
                  ) : type === 'feed_consumption' ? (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.quantity ?? '—'} kg</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.metadata?.feedType || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.metadata?.feedBrand || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.amount ? `${rec.currency || 'KES'} ${rec.amount}` : '—'}</td>
                    </>
                  ) : type === 'health_check' ? (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.quantity ?? 0} {rec.unit || 'birds'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.metadata?.bodyCondition || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{Array.isArray(rec.metadata?.symptoms) ? rec.metadata.symptoms.join(', ') : (rec.metadata?.symptoms || '—')}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{Array.isArray(rec.metadata?.treatments) ? rec.metadata.treatments.join(', ') : (rec.metadata?.treatments || '—')}</td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.quantity ?? '—'} {rec.unit || ''}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.amount ? `${rec.currency || 'KES'} ${rec.amount}` : '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-700">{rec.category || '—'}</td>
                      {type === 'sales' && (
                        <>
                          <td className="px-4 py-3 text-sm text-gray-700">{rec.category === 'eggs' ? (rec.metadata?.trays ?? '—') : '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{rec.category === 'eggs' ? (rec.metadata?.extras ?? '—') : '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{rec.category === 'eggs' ? (rec.metadata?.traySize ?? 30) : '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{rec.category === 'eggs' ? (rec.metadata?.pricePerTray != null ? `KES ${rec.metadata?.pricePerTray}` : '—') : '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{rec.metadata?.customerName || '—'}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{rec.metadata?.customerPhone || '—'}</td>
                          <td className="px-4 py-3 text-sm">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              className="input py-1 px-2 w-24"
                              defaultValue={rec.metadata?.amountPaid ?? 0}
                              onBlur={async (e) => {
                                try {
                                  const amountPaid = Number(e.target.value || 0);
                                  if (amountPaid < 0) return;
                                  const totalAmount = Number(rec.amount || 0);
                                  if (amountPaid > totalAmount) {
                                    alert('Amount paid cannot exceed total');
                                    e.target.value = rec.metadata?.amountPaid ?? 0;
                                    return;
                                  }
                                  const paymentBalance = Math.max(0, totalAmount - amountPaid);
                                  const paymentStatus = paymentBalance <= 0 ? 'paid' : 'pending';
                                  const newMd = { ...(rec.metadata || {}), amountPaid, paymentBalance, paymentStatus };
                                  await recordsAPI.updateRecord(rec.id, { metadata: newMd });
                                  load();
                                } catch (err) {
                                  console.error('Update amount paid error', err);
                                  alert('Failed to update');
                                }
                              }}
                            />
                          </td>
                          <td className={`px-4 py-3 text-sm font-medium ${(rec.metadata?.paymentBalance ?? 0) > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                            KES {rec.metadata?.paymentBalance ?? 0}
                          </td>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              rec.metadata?.paymentStatus === 'paid'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {rec.metadata?.paymentStatus === 'paid' ? '✓ Paid' : '⏳ Pending'}
                            </span>
                          </td>
                        </>
                      )}
                    </>
                  )}
                  <td className="px-4 py-3 text-sm text-gray-700">{rec.description || '—'}</td>
                  <td className="px-4 py-3 text-sm text-right space-x-2">
                    {canEdit ? (
                      <>
                        <button className="btn-outline" onClick={()=> setEditRec(rec)}>Edit</button>
                        <button className="btn-outline" onClick={()=>remove(rec.id, rec)}>Delete</button>
                      </>
                    ) : (
                      <span className="text-xs text-gray-400">Viewer</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </>
        )}
      </div>

      {/* Pagination (desktop) */}
      <div className="hidden md:flex items-center justify-between mt-3">
        <div className="text-sm text-gray-600">
          Page {page} of {Math.max(1, Math.ceil(total / pageSize))} • {total} items
        </div>
        <div className="flex items-center gap-2">
          <select className="input py-1 px-2 text-sm" value={pageSize} onChange={(e)=>{ setPage(1); setPageSize(Number(e.target.value)); }}>
            {[10,20,50,100].map(s => <option key={s} value={s}>{s}/page</option>)}
          </select>
          <button className="btn-outline text-sm" onClick={()=> setPage(p=> Math.max(1, p-1))} disabled={page <= 1}>Prev</button>
          <button className="btn-outline text-sm" onClick={()=> setPage(p=> (p * pageSize >= total ? p : p+1))} disabled={page * pageSize >= total}>Next</button>
        </div>
      </div>

      {showEggAdjust && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-amber-600 to-orange-500 text-white rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold">Adjust egg stock</h3>
              <button className="px-3 py-1 rounded-md bg-white text-amber-700" onClick={()=>setShowEggAdjust(false)}>Close</button>
            </div>
            <form onSubmit={submitEggAdjust} className="p-4 sm:p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Current stock: <span className="font-semibold text-gray-900">{Number(eggStock?.closingBalance || 0).toLocaleString()} eggs</span>
                {cohortId ? ' for this flock' : ' on the farm'}. Set the actual count on hand, or add/remove eggs that were never recorded as a sale.
              </p>
              <div>
                <label className="label">What do you want to do?</label>
                <select className="input" value={eggAdjust.mode} onChange={(e)=>setEggAdjust((p)=>({ ...p, mode: e.target.value }))}>
                  <option value="set">Set stock to this count</option>
                  <option value="add">Add eggs</option>
                  <option value="remove">Remove eggs</option>
                </select>
              </div>
              <div>
                <label className="label">Date *</label>
                <input type="date" required className="input" value={eggAdjust.date} onChange={(e)=>setEggAdjust((p)=>({ ...p, date: e.target.value }))}/>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">Trays</label>
                  <input type="number" min="0" className="input" value={eggAdjust.trays} onChange={(e)=>setEggAdjust((p)=>({ ...p, trays: e.target.value }))}/>
                </div>
                <div>
                  <label className="label">Extras</label>
                  <input type="number" min="0" className="input" value={eggAdjust.extras} onChange={(e)=>setEggAdjust((p)=>({ ...p, extras: e.target.value }))}/>
                </div>
                <div>
                  <label className="label">Tray size</label>
                  <input type="number" min="1" className="input" value={eggAdjust.traySize} onChange={(e)=>setEggAdjust((p)=>({ ...p, traySize: e.target.value }))}/>
                </div>
              </div>
              <div>
                <label className="label">Reason</label>
                <select className="input" value={eggAdjust.reason} onChange={(e)=>setEggAdjust((p)=>({ ...p, reason: e.target.value }))}>
                  {eggAdjustReasons.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Notes</label>
                <textarea rows={2} className="input" value={eggAdjust.description} onChange={(e)=>setEggAdjust((p)=>({ ...p, description: e.target.value }))} placeholder="e.g. leftover eggs from a flock that was closed"/>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" className="btn-outline" onClick={()=>setShowEggAdjust(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={adjustingEggs}>{adjustingEggs ? 'Saving...' : 'Save adjustment'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="w-full max-w-2xl rounded-2xl border border-gray-200 bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-primary-600 to-secondary-500 text-white rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold">Add Record</h3>
              <button className="inline-flex items-center px-3 py-1 rounded-md bg-white text-primary-700 hover:bg-primary-50 shadow" onClick={()=>setShowCreate(false)}>Close</button>
            </div>
            <form onSubmit={onCreate} className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className="label">Type *</label>
                <select name="type" value={formType} onChange={(e)=>setFormType(e.target.value)} required className="input">
                  {typeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="label">Flock</label>
                <select name="cohortId" className="input">
                  <option value="">No specific flock</option>
                  {cohorts.map(c => <option key={c.id} value={c.id}>{c.name} • {c.type}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Date *</label>
                <input type="date" name="date" required className="input"/>
              </div>

              {renderDynamicFields()}

              <div className="md:col-span-2">
                <label className="label">Description</label>
                <textarea name="description" rows={3} className="input" placeholder="Optional notes..."/>
              </div>

              <div className="md:col-span-2 pt-2 flex items-center justify-end gap-3">
                <button type="button" className="btn-outline" onClick={()=>setShowCreate(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={creating}>
                  {creating ? 'Saving...' : 'Save Record'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editRec && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-50">
          <div className="w-full max-w-2xl rounded-2xl border border-gray-200 bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-primary-600 to-secondary-500 text-white rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold">Edit Record</h3>
              <button className="inline-flex items-center px-3 py-1 rounded-md bg-white text-primary-700 hover:bg-primary-50 shadow" onClick={()=>setEditRec(null)}>Close</button>
            </div>
            <form onSubmit={onUpdate} className="p-4 sm:p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="col-span-1 md:col-span-2">
                <label className="label">Type *</label>
                <select name="type" defaultValue={editRec.type} required className="input">
                  {typeOptions.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Date *</label>
                <input type="date" name="date" defaultValue={editRec.date?.slice(0,10)} required className="input"/>
              </div>
              <div>
                <label className="label">Flock</label>
                <select name="cohortId" className="input" defaultValue={editRec.cohortId || ''}>
                  <option value="">No specific flock</option>
                  {cohorts.map(c => <option key={c.id} value={c.id}>{c.name} • {c.type}</option>)}
                </select>
              </div>
              
              {/* Type-specific edit fields */}
              {editRec.type === 'egg_collection' ? (
                <>
                  <div>
                    <label className="label">Trays *</label>
                    <input name="trays" type="number" min="0" className="input" defaultValue={editRec.metadata?.trays ?? 0}/>
                  </div>
                  <div>
                    <label className="label">Tray Size</label>
                    <input name="traySize" type="number" min="1" className="input" defaultValue={editRec.metadata?.traySize ?? 30}/>
                  </div>
                  <div>
                    <label className="label">Extra Eggs</label>
                    <input name="extras" type="number" min="0" className="input" defaultValue={editRec.metadata?.extras ?? 0}/>
                  </div>
                  <div>
                    <label className="label">Broken</label>
                    <input name="broken" type="number" min="0" className="input" defaultValue={editRec.metadata?.broken ?? 0}/>
                  </div>
                  <div className="col-span-1 md:col-span-2 bg-gray-50 p-3 rounded-lg">
                    <div className="text-sm text-gray-600">Current Total: <span className="font-semibold text-gray-900">{editRec.quantity ?? 0} eggs</span></div>
                  </div>
                </>
              ) : editRec.type === 'feed_consumption' ? (
                <>
                  <div>
                    <label className="label">Quantity (kg) *</label>
                    <input name="quantity" type="number" step="any" min="0" className="input" defaultValue={editRec.quantity ?? ''}/>
                  </div>
                  <div>
                    <label className="label">Feed Type</label>
                    <select name="feedType" className="input" defaultValue={editRec.metadata?.feedType || ''}>
                      <option value="">Select...</option>
                      {feedTypes.map(ft => <option key={ft} value={ft}>{ft}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Feed Brand</label>
                    <select name="feedBrand" className="input" defaultValue={editRec.metadata?.feedBrand || ''}>
                      <option value="">Select...</option>
                      {feedBrandsKE.map(b => <option key={b} value={b}>{b}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Amount (KES)</label>
                    <input name="amount" type="number" step="0.01" className="input" defaultValue={editRec.amount ?? ''}/>
                  </div>
                </>
              ) : editRec.type === 'health_check' ? (
                <>
                  <div>
                    <label className="label">Affected Birds *</label>
                    <input name="affected" type="number" min="0" className="input" defaultValue={editRec.quantity ?? 0}/>
                  </div>
                  <div>
                    <label className="label">Body Condition</label>
                    <select name="bodyCondition" className="input" defaultValue={editRec.metadata?.bodyCondition || ''}>
                      <option value="">Select...</option>
                      {['good','fair','poor'].map(x => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </div>
                  <div className="col-span-1 md:col-span-2">
                    <label className="label">Symptoms (comma separated)</label>
                    <input name="symptoms" className="input" defaultValue={Array.isArray(editRec.metadata?.symptoms) ? editRec.metadata.symptoms.join(', ') : (editRec.metadata?.symptoms || '')}/>
                  </div>
                  <div className="col-span-1 md:col-span-2">
                    <label className="label">Treatment/Actions (comma separated)</label>
                    <input name="treatments" className="input" defaultValue={Array.isArray(editRec.metadata?.treatments) ? editRec.metadata.treatments.join(', ') : (editRec.metadata?.treatments || '')}/>
                  </div>
                </>
              ) : editRec.type === 'sales' ? (
                <>
                  <div>
                    <label className="label">Category</label>
                    <select name="category" className="input" defaultValue={editRec.category || 'eggs'}>
                      {salesCategories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  {editRec.category === 'eggs' || !editRec.category ? (
                    <>
                      <div>
                        <label className="label">Trays</label>
                        <input name="eggTrays" type="number" min="0" className="input" defaultValue={editRec.metadata?.trays ?? ''}/>
                      </div>
                      <div>
                        <label className="label">Extra Eggs</label>
                        <input name="eggExtras" type="number" min="0" className="input" defaultValue={editRec.metadata?.extras ?? ''}/>
                      </div>
                      <div>
                        <label className="label">Tray Size</label>
                        <input name="eggTraySize" type="number" min="1" className="input" defaultValue={editRec.metadata?.traySize ?? 30}/>
                      </div>
                      <div>
                        <label className="label">Price per Tray (KES)</label>
                        <input name="eggTrayPrice" type="number" step="0.01" min="0" className="input" defaultValue={editRec.metadata?.pricePerTray ?? ''}/>
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="label">Quantity</label>
                        <input name="quantity" type="number" step="any" min="0" className="input" defaultValue={editRec.quantity ?? ''}/>
                      </div>
                      <div>
                        <label className="label">Unit Price (KES)</label>
                        <input name="unitPrice" type="number" step="0.01" min="0" className="input"/>
                      </div>
                    </>
                  )}
                  <div>
                    <label className="label">Amount (KES)</label>
                    <input name="amount" type="number" step="0.01" className="input" defaultValue={editRec.amount ?? ''}/>
                  </div>
                  <div>
                    <label className="label">Customer Name</label>
                    <input name="customerName" className="input" defaultValue={editRec.metadata?.customerName ?? ''}/>
                  </div>
                  <div>
                    <label className="label">Customer Phone</label>
                    <input name="customerPhone" className="input" defaultValue={editRec.metadata?.customerPhone ?? ''}/>
                  </div>
                  <div>
                    <label className="label">Amount Paid</label>
                    <input name="amountPaid" type="number" step="0.01" min="0" className="input" defaultValue={editRec.metadata?.amountPaid ?? 0}/>
                    <p className="text-xs text-gray-500 mt-1">
                      Balance & status auto-calculated from total amount
                    </p>
                  </div>
                </>
              ) : editRec.type === 'expense' ? (
                <>
                  <div>
                    <label className="label">Category *</label>
                    <select name="category" className="input" defaultValue={editRec.category || 'other'}>
                      {expenseCategories.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label">Amount (KES) *</label>
                    <input name="amount" type="number" step="0.01" min="0" required className="input" defaultValue={editRec.amount ?? ''}/>
                  </div>
                </>
              ) : editRec.type === 'mortality' ? (
                <>
                  <div className="col-span-1 md:col-span-2">
                    <label className="label">Birds Lost *</label>
                    <input name="quantity" type="number" min="0" required className="input" defaultValue={Math.abs(editRec.quantity ?? 0)}/>
                  </div>
                </>
              ) : editRec.type === 'stock' ? (
                <>
                  <div>
                    <label className="label">Quantity *</label>
                    <input name="quantity" type="number" step="any" required className="input" defaultValue={editRec.quantity ?? ''}/>
                  </div>
                  <div>
                    <label className="label">Category</label>
                    <select name="category" className="input" defaultValue={editRec.category || 'other'}>
                      {stockCategories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="label">Quantity</label>
                    <input name="quantity" type="number" step="any" className="input" defaultValue={editRec.quantity ?? ''}/>
                  </div>
                  <div>
                    <label className="label">Amount</label>
                    <input name="amount" type="number" step="0.01" className="input" defaultValue={editRec.amount ?? ''}/>
                  </div>
                  <div>
                    <label className="label">Category</label>
                    <input name="category" className="input" defaultValue={editRec.category || ''}/>
                  </div>
                </>
              )}
              
              <div className="col-span-1 md:col-span-2">
                <label className="label">Description</label>
                <textarea name="description" rows={2} className="input" defaultValue={editRec.description || ''}/>
              </div>
              <div className="col-span-1 md:col-span-2 pt-2 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                <button
                  type="button"
                  className="btn-danger order-2 sm:order-1"
                  onClick={async ()=>{ if (!editRec) return; const deleted = await remove(editRec.id, editRec); if (deleted) setEditRec(null); }}
                >
                  Delete Record
                </button>
                <div className="flex items-center gap-3 order-1 sm:order-2">
                  <button type="button" className="btn-outline flex-1 sm:flex-none" onClick={()=>setEditRec(null)}>Cancel</button>
                  <button type="submit" className="btn-primary flex-1 sm:flex-none">Update</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      {canEdit && (
        <button
          className="md:hidden fixed bottom-6 right-6 z-50 rounded-full bg-primary-600 text-white p-4 shadow-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          aria-label="Add Record"
          onClick={()=>{ setShowCreate(true); setFormType('egg_collection'); }}
        >
          <PlusIcon className="h-6 w-6" />
        </button>
      )}
    </div>
  );
};

export default Records;
